import { FC, useEffect, useState } from 'react';
import { Button, Modal, Space, Spin } from 'antd';
import dayjs from 'dayjs';
import { useMethods } from '@lhb/hook';
import V2FormRangeTimePicker from '@/common/components/Form/V2FormRangeTimePicker/V2FormRangeTimePicker';
import V2FormCheckbox from '@/common/components/Form/V2FormCheckbox/V2FormCheckbox';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import { getCheckSpotAnalysisPeriods } from '@/common/api/expandStore/checkSpotTaskCreate';
import styles from './CreateCheckSpotTaskModal.module.less';

type Period = { start: string; end: string };

/** 最终值保留离散时段，范围控件仅显示其起止边界。 */
const CheckSpotPeriodSelector: FC<{ value?: Period[]; onChange?: (value: Period[]) => void; disabled?: boolean }> = ({ value = [], onChange, disabled }) => {
  const [options, setOptions] = useState<Period[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>([]);
  const sorted = value.slice().sort((a, b) => a.start.localeCompare(b.start));
  const methods = useMethods({
    load() {
      setLoading(true);
      setLoadFailed(false);
      return getCheckSpotAnalysisPeriods().then(res => {
        const fixed = Array.isArray(res?.data?.fixedPeriods) ? res.data.fixedPeriods : [];
        const periods: Period[] = fixed.flatMap(item => Array.isArray(item.periods) ? item.periods : []);
        setOptions(periods.filter((item, index) => periods.findIndex(other => other.start === item.start && other.end === item.end) === index).sort((a, b) => a.start.localeCompare(b.start)));
      }, () => { setOptions([]); setLoadFailed(true); }).finally(() => setLoading(false));
    },
    changeRange(_dates, times: [string, string]) {
      onChange?.(options.filter(item => item.start >= times[0] && item.end <= times[1]));
    },
    show() {
      setDraft(value.map(item => `${item.start}-${item.end}`));
      setOpen(true);
    },
    close() { setOpen(false); },
    changeDraft(values) { setDraft(values); },
    toggleAll() { setDraft(draft.length === options.length ? [] : options.map(item => `${item.start}-${item.end}`)); },
    confirm() {
      if (!draft.length) { V2Message.warning('请选择采集时段'); return; }
      onChange?.(options.filter(item => draft.includes(`${item.start}-${item.end}`)));
      setOpen(false);
    },
  });
  useEffect(() => { methods.load(); }, []);
  return <Spin spinning={loading}>
    <Space align='start' wrap>
      <V2FormRangeTimePicker noStyle disabled={disabled || !options.length} config={{
        format: 'HH:mm',
        value: sorted.length ? [dayjs(`2000-01-01T${sorted[0].start}`), dayjs(`2000-01-01T${sorted[sorted.length - 1].end}`)] as any : null,
        onChange: methods.changeRange,
      }} />
      <Button type='link' disabled={disabled || !options.length} onClick={methods.show}>自定义时段</Button>
      {!loading && loadFailed && <Button type='link' disabled={disabled} onClick={methods.load}>重新加载采集时段</Button>}
    </Space>
    {!!sorted.length && <div className={styles.periodSummary}>已选：{sorted.map(item => `${item.start}-${item.end}`).join('、')}</div>}
    <Modal open={open && !disabled} title='自定义时段' width={520} getContainer={false} onCancel={methods.close} onOk={methods.confirm} destroyOnClose>
      <Button type='link' onClick={methods.toggleAll}>{draft.length === options.length ? '取消全选' : '全选'}</Button>
      <V2FormCheckbox noStyle options={options.map(item => ({ label: `${item.start}-${item.end}`, value: `${item.start}-${item.end}` }))} config={{ value: draft, onChange: methods.changeDraft, className: styles.periodOptions }} />
    </Modal>
  </Spin>;
};

export default CheckSpotPeriodSelector;
